/**
 * GridRow — CSS Grid Engine for Canvas v2.0
 *
 * architectural refactor: all stylable / responsive
 * properties (padding, margin, backgroundColor, border, border-radius,
 * box-shadow) emit into a generated <style> block keyed by row id —
 * NEVER as inline styles. This removes the prior reliance on `!important`
 * to override inline desktop values from media queries (project rule:
 * NO `!important` in CSS — EVER. Increase specificity instead — but here
 * we don't even need higher specificity, just source order in the same
 * <style> block, since all rules sit at 0,1,0).
 *
 * Inline styles are kept ONLY for layout-only properties that aren't
 * responsive-overridable in the editor (display:grid, gridTemplateColumns,
 * gap, width:100%, alignItems, textAlign, position/zIndex for header rows,
 * overflow). Width/maxWidth go inline because the desktop max-width is
 * also overridden via the mobile media-query rule below — both target the
 * same selector, mobile wins via cascade.
 *
 * Responsive overrides (any subset works — missing values inherit from
 * the desktop base):
 *   paddingMobile / mobilePadding   : { top, right, bottom, left }
 *   paddingTablet / tabletPadding   : { top, right, bottom, left }
 *   marginMobile  / mobileMargin    : { top, right, bottom, left }
 *   marginTablet  / tabletMargin    : { top, right, bottom, left }
 *   mobileMinHeight / tabletMinHeight / desktopMinHeight : string
 */
import React from 'react';
import type { BlockComponentProps } from '../../renderer/BlockRegistry';
import { buildRowGridTemplateColumns } from '@agentic/magento-client/lib/grid-tracks';

type BoxSides = { top?: number; right?: number; bottom?: number; left?: number };

function formatBoxSides(val: unknown, prop: 'padding' | 'margin'): string {
  if (val == null) return '';
  if (typeof val === 'string') {
    return `${prop}: ${val};`;
  }
  if (typeof val === 'object') {
    const v = val as BoxSides;
    const parts: string[] = [];
    if (v.top != null) parts.push(`${prop}-top: ${v.top}px;`);
    if (v.right != null) parts.push(`${prop}-right: ${v.right}px;`);
    if (v.bottom != null) parts.push(`${prop}-bottom: ${v.bottom}px;`);
    if (v.left != null) parts.push(`${prop}-left: ${v.left}px;`);
    return parts.join(' ');
  }
  return '';
}

function decl(prop: string, val: unknown): string {
  if (val == null || val === '') return '';
  if (typeof val === 'number') return `${prop}: ${val}px;`;
  if (typeof val === 'string') return `${prop}: ${val};`;
  return '';
}

function formatRadius(val: unknown): string | undefined {
  if (val == null) return undefined;
  if (typeof val === 'number') return `${val}px`;
  if (typeof val !== 'string') return undefined;
  const s: string = val;
  if (/[a-zA-Z(%]/.test(s)) return s;
  const n = Number(s);
  return isNaN(n) ? s : `${n}px`;
}

export const GridRow: React.FC<BlockComponentProps & { hoverStyles?: string, 'data-block-type'?: string, 'data-block-id'?: string }> = ({ data, children, style, hoverStyles, className, 'data-block-type': dataBlockType, 'data-block-id': dataBlockId }) => {
  // Padding / margin (desktop + responsive overrides)
  const rawPadding = data?.padding;
  const rawMargin  = data?.margin;
  const mobilePadding = (data?.paddingMobile || data?.mobilePadding) as BoxSides | string | undefined;
  const tabletPadding = (data?.paddingTablet || data?.tabletPadding) as BoxSides | string | undefined;
  const mobileMargin  = (data?.marginMobile || data?.mobileMargin)  as BoxSides | string | undefined;
  const tabletMargin  = (data?.marginTablet || data?.tabletMargin)  as BoxSides | string | undefined;
  const mobileMinHeight  = data?.mobileMinHeight  as string | undefined;
  const tabletMinHeight  = data?.tabletMinHeight  as string | undefined;
  const desktopMinHeight = data?.desktopMinHeight as string | undefined;

  // Mobile-only chrome (matches Column.tsx pattern). Lets a row become a
  // self-contained card on phone width without changing desktop. Both naming
  // conventions accepted (mobileBackgroundColor and backgroundColorMobile).
  const mobileBg     = (data?.mobileBackgroundColor ?? data?.backgroundColorMobile) as string | undefined;
  const mobileBorder = (data?.mobileBorder         ?? data?.borderMobile)           as string | undefined;
  const mobileRadius = (data?.mobileBorderRadius   ?? data?.borderRadiusMobile)     as string | number | undefined;
  const mobileShadow = (data?.mobileBoxShadow      ?? data?.boxShadowMobile)        as string | undefined;

  // Responsive gap — lets a row use a tight gap when stacked on phone but a
  // generous one on desktop. Accepts both naming conventions.
  const mobileGap = (data?.gapMobile ?? data?.mobileGap) as string | number | undefined;
  const tabletGap = (data?.gapTablet ?? data?.tabletGap) as string | number | undefined;

  // The edge-gutter rule needs to know if the desktop padding is an object
  // form so it can max() against the explicit left/right values.
  const padObj = (rawPadding && typeof rawPadding === 'object') ? rawPadding as BoxSides : undefined;
  // Same trick for desktop margin — used by the shadow-clip protection rule
  // below to max() the user's bottom margin against --style-card-shadow-spread.
  const marginObj = (rawMargin && typeof rawMargin === 'object') ? rawMargin as BoxSides : undefined;

  // Grid template
  const columnDefs: Array<{ width?: string }> = Array.isArray(data?.columns) ? data.columns as Array<{ width?: string }> : [];
  const childCount = React.Children.count(children);

  // 2026-05-28 PR F #2/3/11/13 — drop the previous `isUniformFr` gate.
  // The redistribute rule below now fires for ANY row that has an empty
  // column, regardless of whether the row used 1fr / 2fr / pixel widths.
  // Rationale: merchants set widths assuming all columns render. When one
  // collapses (CMS block missing, conditional block returned null), the
  // visual intent is "fill the row" — auto-fit with a sensible min lets
  // the remaining columns expand to absorb the freed track without the
  // pixel-fixed columns getting pinned to the left edge.
  // Opt-out: `data.preserveEmpty: true` (or `settings.preserveEmpty`)
  // skips BOTH the row-collapse rule and the auto-fit redistribute, so
  // intentional empty rows / spacer columns stay exactly as designed.
  const rowSettings: Record<string, unknown> | null = (() => {
    const s = data?.settings;
    return s && typeof s === 'object' ? (s as Record<string, unknown>) : null;
  })();
  const preserveEmpty: boolean =
    data?.preserveEmpty === true || rowSettings?.preserveEmpty === true;

  // Auto-distribute (flex) mode — opt-in via `distribute: 'fill-center'`
  // The default `display:grid` path uses a FIXED track list, which can never
  // reflow a vacated track: when a column is hidden (empty CMS block / removed
  // in admin) the grid leaves a ghost gap and the survivors hug their original
  // tracks. The historical `:has() + auto-fit` redistribute (still used by the
  // grid path below) left-aligns rather than centering and can't honour fr
  // ratios. `fill-center` switches the row to flexbox: visible columns grow to
  // share the full row width (so 3 of 5 columns expand to fill, group centered),
  // hidden columns drop out of flex flow automatically. This is what lets ONE
  // master layout declare every CMS slot and auto-adapt as blocks are added or
  // removed. Everything below is gated on this flag — grid behaviour is
  // completely untouched when it is off.
  const distributeRaw =
    (typeof data?.distribute === 'string' ? data.distribute : undefined) ??
    (typeof rowSettings?.distribute === 'string' ? (rowSettings.distribute) : undefined) ??
    (data?.distribute === true || rowSettings?.distribute === true ? 'fill-center' : undefined);
  const isFillCenter = distributeRaw === 'fill-center' || distributeRaw === 'fill';
  // 'manual' — every stored width honoured exactly, nothing stretches into the
  // slack. See buildManualGridTracks for the measured behaviour.
  const columnDistribution = distributeRaw === 'manual' ? 'manual' : 'fixed';

  // Same expression as `gapVal` further down — declared early because the
  // track builder needs the gutter size to carve it back out of percentage
  // tracks. Kept as a separate const rather than hoisting `gapVal` so nothing
  // in this component's long declaration order has to move.
  const rowGapRaw: string | number =
    (typeof data?.gap === 'string' || typeof data?.gap === 'number') ? data.gap : '0';

  // buildGridTracks is shared with the editor canvas so both renderers agree.
  // It returns the stored widths verbatim EXCEPT for the one shape that
  // overflows: a row where every track is a percentage and they sum to a full
  // row. See packages/magento-client/src/lib/grid-tracks.ts.
  const desktopWidths = columnDefs.map(c => {
    const w = c.width || '1fr';
    return typeof w === 'object' ? ((w as { d?: string }).d ?? '1fr') : w;
  });

  const explicitGridTemplate = columnDefs.length > 0
    ? buildRowGridTemplateColumns(desktopWidths, rowGapRaw, columnDistribution)
    : childCount > 0 ? `repeat(${childCount}, 1fr)` : '1fr';

  const gridTemplateColumns = explicitGridTemplate;

  const dataId: string | undefined =
    typeof data?.id === 'string' || typeof data?.id === 'number'
      ? String(data.id)
      : (typeof dataBlockId === 'string' && dataBlockId) ? dataBlockId : undefined;
  // Stable React id as last resort — Math.random differs between SSR and client
  // and strands the <style> selector pointing at an id the client tree never produces.
  const fallbackId = React.useId().replace(/:/g, '');
  const validRowId: string = dataId ? `grid-${dataId}` : `grid-${fallbackId}`;

  // Borders (preserve existing sanitisation)
  function adaptBorder(val?: string): string | undefined {
    if (!val) return undefined;
    return val.replace(/var\(--glass-border\)/g, 'var(--style-divider-color, var(--glass-border))');
  }
  const borderBottom = adaptBorder(typeof data?.borderBottom === 'string' ? data.borderBottom : undefined);
  const borderTop    = adaptBorder(typeof data?.borderTop === 'string' ? data.borderTop : undefined);

  // BUGFIX: older saved layouts ship the FULL shorthand token in
  // `borderColor` (e.g. 'var(--style-card-border, …color-mix…)').
  // CSS rejects shorthand on `border-color:` and falls back to
  // currentColor. Defensive remap to the colour-only token.
  const sanitizedBorderColor = (() => {
    const v = data?.borderColor;
    if (!v || typeof v !== 'string') return v || undefined;
    const trimmed = v.trim();
    if (
      trimmed.startsWith('var(--style-card-border') ||
      /\bsolid\b/.test(trimmed) ||
      /\bdashed\b/.test(trimmed) ||
      /\b\d+(\.\d+)?(px|rem|em)\b/.test(trimmed)
    ) {
      return 'var(--glass-border)';
    }
    return trimmed;
  })();

  // Per-corner border-radius support
  const hasPerCornerRadius =
    data?.borderTopLeftRadius != null ||
    data?.borderTopRightRadius != null ||
    data?.borderBottomLeftRadius != null ||
    data?.borderBottomRightRadius != null;
  const radiusDecls = hasPerCornerRadius
    ? [
        decl('border-top-left-radius',     formatRadius(data?.borderTopLeftRadius)),
        decl('border-top-right-radius',    formatRadius(data?.borderTopRightRadius)),
        decl('border-bottom-left-radius',  formatRadius(data?.borderBottomLeftRadius)),
        decl('border-bottom-right-radius', formatRadius(data?.borderBottomRightRadius)),
      ].filter(Boolean).join(' ')
    : decl('border-radius', formatRadius(data?.borderRadius));

  // Wrap mode: when row has a backgroundColor and DOESN'T explicitly opt
  // out via useGlobal:false, wrap it in an outer full-width div so the bg
  // colour spans edge-to-edge. The inner grid still respects maxWidth.
  const needsWrapper = !!data?.backgroundColor && data?.useGlobal !== false;
  const innerMaxWidth: string | undefined = typeof data?.maxWidth === 'string' ? data.maxWidth : undefined;
  // FULL-WIDTH (full-bleed) row: break the row out of its container so it spans
  // the entire viewport (100vw), regardless of the page's --style-container-max.
  // Accepts BOTH key names: `fullWidth` (canonical, what the editor Row control
  // sets) and `fullBleed` (alias used by some presets). The inner content stays
  // readable because columns keep their own widths / the hero variant centres
  // its text. A full-width row also opts OUT of the 24px desktop edge-gutter and
  // the container max-width clamp below — otherwise it'd still sit at 1600px with
  // side padding. The 100vw breakout (width:100vw; margin-left:calc(50% - 50vw))
  // is emitted in the generated CSS so it survives prerender + has no JS cost.
  const isFullBleed = data?.fullWidth === true || data?.fullBleed === true;
  const animationRaw = data?.animation;
  const entryAnimType = (data as { entryAnimation?: { type?: string } } | undefined)?.entryAnimation?.type;
  const animationFallback: string | undefined = entryAnimType && entryAnimType !== 'none' ? entryAnimType : undefined;
  const animationClass: string | undefined = typeof animationRaw === 'string' ? animationRaw : animationFallback;

  // Build generated CSS
  // INNER (grid) selector receives: grid-template, padding, border-radius,
  // margin, maxWidth — the things that govern the inner card box.
  // OUTER (wrapper) selector receives: backgroundColor + border, so the
  // colour spans full-width while the inner grid stays constrained.
  // When NOT needsWrapper, ALL declarations land on the inner selector.
  const innerSel = `#${validRowId}`;
  const outerSel = needsWrapper ? `#wrapper-${validRowId}` : innerSel;

  // Inner desktop declarations (always on inner selector)
  const innerDesktopDecls: string[] = [
    // In fill-center (flex) mode there are no grid tracks — column sizing is
    // handled by flex-grow rules emitted alongside the display rule below.
    isFillCenter ? '' : `grid-template-columns: ${gridTemplateColumns};`,
    formatBoxSides(rawPadding, 'padding'),
    radiusDecls,
  ];
  // maxWidth + horizontal centering: when wrapped, the inner grid gets the
  // maxWidth + auto centring (since the wrapper is full width). When not
  // wrapped, maxWidth respects the row's own setting.
  if (needsWrapper) {
    innerDesktopDecls.push(`max-width: ${innerMaxWidth ?? '100%'};`);
    if (rawMargin === undefined) innerDesktopDecls.push('margin: 0 auto;');
    else innerDesktopDecls.push(formatBoxSides(rawMargin, 'margin'));
  } else if (isFullBleed) {
    // 100vw breakout — span the viewport irrespective of any container max.
    // margin-left:calc(50% - 50vw) pulls the row's left edge back to the
    // viewport edge from wherever its normal-flow position is; matching
    // margin-right keeps it symmetric. max-width:none defeats any inherited
    // clamp. Inner columns/content keep their own widths so text isn't stretched.
    innerDesktopDecls.push('max-width: none;');
    innerDesktopDecls.push('width: 100vw;');
    // VERTICAL margins come from the row's own `margin` setting (top/bottom);
    // HORIZONTAL margins are forced to the breakout calc. Emitting top/bottom
    // explicitly (default 0) guarantees a full-bleed hero sits flush against the
    // row above (e.g. the trust bar) with ZERO top gap — the inline shorthand
    // would otherwise reset all four sides and the calc()s below would clobber a
    // designer's top margin. We read rawMargin's vertical sides when present.
    const mt = (rawMargin && typeof rawMargin === 'object' && 'top' in rawMargin) ? `${(rawMargin as { top?: number }).top ?? 0}px` : '0';
    const mb = (rawMargin && typeof rawMargin === 'object' && 'bottom' in rawMargin) ? `${(rawMargin as { bottom?: number }).bottom ?? 0}px` : '0';
    innerDesktopDecls.push(`margin-top: ${mt};`);
    innerDesktopDecls.push(`margin-bottom: ${mb};`);
    innerDesktopDecls.push('margin-left: calc(50% - 50vw);');
    innerDesktopDecls.push('margin-right: calc(50% - 50vw);');
  } else {
    if (typeof data?.maxWidth === 'string' && data.maxWidth) innerDesktopDecls.push(`max-width: ${data.maxWidth};`);
    else if (data?.fullWidth) innerDesktopDecls.push('max-width: 100%;');
    innerDesktopDecls.push(formatBoxSides(rawMargin, 'margin'));
  }

  // Outer/inner styling decls — colour, border, shadow.
  // When wrapped these go on outer; when not, they go on inner.
  const styleDecls: string[] = [
    decl('background-color', data?.backgroundColor),
    decl('border-bottom', borderBottom),
    decl('border-top',    borderTop),
    decl('border',        data?.border),
    decl('border-width',  (() => {
      const bw = data?.borderWidth;
      if (bw == null) return undefined;
      const asStr = typeof bw === 'string' ? bw : typeof bw === 'number' ? String(bw) : '';
      if (!asStr) return undefined;
      return isNaN(Number(asStr)) ? asStr : `${Number(asStr)}px`;
    })()),
    decl('border-style',  data?.borderStyle),
    decl('border-color',  sanitizedBorderColor),
    decl('box-shadow',    data?.boxShadow),
  ].filter(Boolean);

  const cssBlocks: string[] = [];
  if (innerDesktopDecls.filter(Boolean).length > 0) {
    cssBlocks.push(`${innerSel} { ${innerDesktopDecls.filter(Boolean).join(' ')} }`);
  }
  if (styleDecls.length > 0) {
    cssBlocks.push(`${outerSel} { ${styleDecls.join(' ')} }`);
  }

  // Empty-row + empty-column collapse (PR F #2/3/11/13, 2026-05-28)
  //
  // Background:
  //   BlockRenderer always emits `<div class="is-layout-container"
  //   data-block-type="X">` around every leaf block — even when the leaf's
  //   render fn returns null (e.g. RichText for a disabled / empty CMS
  //   block). The wrapper itself is then truly :empty, but the column
  //   wrapper around it is NOT (it contains the empty leaf wrapper as a
  //   child), and the row wrapper is NOT (it contains the column wrapper).
  //   Tailwind's `empty:hidden` therefore never fires above the leaf
  //   level, leaving a 12px-tall sliver of row background visible (e.g.
  //   the orange header-promo-strip slab that's been the symptom).
  //
  // Fix: use :has() to look DEEP for a non-empty leaf-block wrapper.
  //   "leaf-block wrapper" = any `[data-block-type]` element that is NOT
  //   a structural type (row / column / section / container). The :not(:empty)
  //   check ensures the wrapper has rendered some content — a disabled
  //   CMS block leaves an empty wrapper and is therefore treated as "no
  //   real content".
  //
  // Selector specificity 0,1,1,0 (id + class) outranks the base
  // 0,1,0,0 grid rule above, so display:none / grid-template-columns
  // overrides win via specificity rather than !important.
  //
  // Opt-out: `data.preserveEmpty: true` (or `settings.preserveEmpty`)
  // skips BOTH rules and preserves the row exactly as designed.
  //
  // Browser support: :has() is Chrome 105+, Firefox 121+, Safari 15.4+ —
  // older browsers degrade to the original explicit template (ghost
  // tracks remain visible), which is acceptable per project policy.

  // Reusable predicate: "row :has() at least one non-empty leaf-block".
  // A leaf-block is identified by `[data-block-type]` whose value is NOT
  // a structural container type. Excluded values match the
  // STRUCTURAL_TYPES set in BlockRenderer.tsx.
  const nonEmptyLeafPred = `[data-block-type]:not([data-block-type="row"]):not([data-block-type="grid_row"]):not([data-block-type="column"]):not([data-block-type="col"]):not([data-block-type="section"]):not([data-block-type="container"]):not(:empty)`;

  if (!preserveEmpty) {
    // 1) Row-level collapse — hide the row entirely when none of its
    //    descendant leaf-block wrappers contain rendered content.
    cssBlocks.push(`${innerSel}:not(:has(${nonEmptyLeafPred})) { display: none; margin: 0; padding: 0; }`);
    if (needsWrapper) {
      cssBlocks.push(`#wrapper-${validRowId}:has(> ${innerSel}:not(:has(${nonEmptyLeafPred}))) { display: none; margin: 0; padding: 0; }`);
    }

    // 2) Column-level collapse — hide any column track inside the row
    //    that contains no non-empty leaf-block. This lets the next rule
    //    (auto-fit redistribute) reclaim the freed space without
    //    leaving a ghost column track.
    cssBlocks.push(`${innerSel} > .is-layout-container:not(:has(${nonEmptyLeafPred})) { display: none; }`);
    // Legacy truly-empty case (column wrapper with zero children at all)
    // still hides, for completeness.
    cssBlocks.push(`${innerSel} > .is-layout-container:empty { display: none; }`);
    // Fill-center (flex) only: a column the merchant never added a block to is
    // structurally `:empty` (zero children). Collapse it with !important so it
    // drops out of the flex flow and the survivors fill+center. We DELIBERATELY
    // do NOT !important the content-sensitive `:not(:has(nonEmptyLeaf))` rule
    // here: a CMS block that is briefly empty during hydration would otherwise
    // get latched to display:none and never reappear once its content loads.
    // (Column.tsx sets an inline display:flex that the non-!important rule
    // above can't beat, but tolerating an off-flow empty wrapper is far safer
    // than risking a real, still-loading column being permanently hidden.)
    if (isFillCenter) {
      cssBlocks.push(`${innerSel} > .is-layout-container:empty { display: none !important; }`);
      // A column whose ONLY child is an empty leaf-block wrapper (e.g. a
      // cms_block that resolved to no content because the merchant never set
      // its identifier) is not structurally :empty, so the rule above misses
      // it. Collapse a column that contains exactly one child which is itself
      // :empty. This is still content-state safe: it keys off a wrapper with
      // ZERO children (a block that rendered nothing at all), not off text
      // presence, so a CMS block that is merely loading is never hidden.
      cssBlocks.push(`${innerSel} > .is-layout-container:has(> .is-layout-container:only-child:empty) { display: none !important; }`);
    }

    // 3) Auto-fit redistribute — when ANY column has no non-empty leaf
    //    (so the rule above hid it), swap the grid-template-columns to
    //    repeat(auto-fit, minmax(min(280px, 100%), 1fr)) so the remaining
    //    columns expand to fill the row instead of hugging the left edge.
    //    Drop the previous `isUniformFr` gate: the merchant's pixel /
    //    fr ratios were intended for the FULL column set; with one
    //    collapsed the visual intent is "fill the row".
    // Grid path only — flex mode redistributes natively (hidden columns drop
    // out of the flex flow and survivors grow to fill), so skip the auto-fit
    // track swap entirely when fill-center is active.
    if (!isFillCenter) {
      cssBlocks.push(`${innerSel}:has(> .is-layout-container:not(:has(${nonEmptyLeafPred}))) { grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr)); }`);
    }
  }

  // Hover styles — preserve placeholder swap behaviour.
  const hoverCssString = hoverStyles
    ? hoverStyles.replace(/\{\{SELECTOR\}\}/g, needsWrapper ? `#wrapper-${validRowId}` : innerSel)
    : '';
  if (hoverCssString) cssBlocks.push(hoverCssString);

  // Stack-on-mobile / tablet column collapse
  const stackOnMobile = data?.stackOnMobile !== false;
  // A 3+ column row collapses to a fixed 1fr 1fr on tablet, so only the 2-column
  // case can still carry percentages through — run it past the same track
  // builder (with the tablet gap, which may differ from desktop) so a full-row
  // pair can't overflow at tablet width either.
  const tabletGridTemplate = columnDefs.length >= 3
    ? '1fr 1fr'
    : buildRowGridTemplateColumns(
        columnDefs.map(c => {
          const w = c.width || '1fr';
          if (typeof w === 'object') return (w as { t?: string }).t ?? '1fr';
          return typeof w === 'string' && w.includes('px') ? '1fr' : w;
        }),
        tabletGap ?? rowGapRaw,
        columnDistribution
      );

  if (stackOnMobile && isFillCenter) {
    // Flex mode: grid-template-columns is meaningless. To stack cleanly on
    // mobile we must FULLY reset the desktop fill-center column rules, not just
    // set flex-basis. The desktop rules (above) set on each column:
    //   `flex: 1 1 0; min-width: min(220px, 100%);`  + per-column `flex-grow:Nfr`
    // `flex-basis:100%` ALONE doesn't stack them, because `min-width:220px`
    // floors each column at 220px → with 4-5 columns on a ~390px viewport they
    // can't shrink, so flex-wrap packs them as rigid overlapping 220px strips
    // (the live footer bug). Reset min-width:0 + flex:0 0 100% so every column
    // takes a full row and they stack one-per-line. !important beats the
    // per-column flex-grow:Nfr rules that are otherwise more specific/later.
    cssBlocks.push(`@media (max-width: 768px) { ${innerSel} > .is-layout-container { flex: 0 0 100% !important; min-width: 0 !important; max-width: 100% !important; } ${innerSel} { gap: 16px; } }`);
  } else if (stackOnMobile) {
    cssBlocks.push(`@media (min-width: 769px) and (max-width: 1024px) { ${innerSel} { grid-template-columns: ${tabletGridTemplate}; } }`);
    cssBlocks.push(`@media (max-width: 768px) { ${innerSel} { grid-template-columns: 1fr; gap: 16px; } }`);
  } else {
    cssBlocks.push(`@media (max-width: 768px) { ${innerSel} { gap: 8px; } }`);
  }

  // Mobile padding / margin / sizing
  // Same selector spec as the base inner rule; later in source order so
  // it wins inside the media-query viewport. No !important.
  const mobileInnerDecls: string[] = [
    'max-width: 100%;',
    // Horizontal padding is settings-only — paddingMobile from the preset
    // is the single source of truth. No hardcoded fallback: rows with no
    // paddingMobile.left/right go edge-to-edge against marginMobile.
    formatBoxSides(mobilePadding, 'padding'),
    mobileMinHeight ? `min-height: ${mobileMinHeight};` : '',
    // Mobile-only chrome (per-row card on phone, no effect on desktop).
    decl('background-color', mobileBg),
    decl('border', mobileBorder),
    decl('border-radius', formatRadius(mobileRadius)),
    decl('box-shadow', mobileShadow),
  ].filter(Boolean);
  // Append mobile gap if set (overrides the base inline gap inside the @media)
  if (mobileGap != null && mobileGap !== '') {
    const gapVal = typeof mobileGap === 'number' ? `${mobileGap}px` : String(mobileGap);
    mobileInnerDecls.push(`gap: ${gapVal};`);
  }
  cssBlocks.push(`@media (max-width: 768px) { ${innerSel} { ${mobileInnerDecls.join(' ')} } }`);

  if (mobileMargin) {
    const marginCss = formatBoxSides(mobileMargin, 'margin');
    if (marginCss) {
      cssBlocks.push(`@media (max-width: 768px) { ${innerSel}, #wrapper-${validRowId} { ${marginCss} } }`);
    }
  }

  // Tablet padding / margin / sizing
  if (tabletPadding || tabletMinHeight || tabletGap != null) {
    const tabletDecls: string[] = [
      formatBoxSides(tabletPadding, 'padding'),
      tabletMinHeight ? `min-height: ${tabletMinHeight};` : '',
      tabletGap != null && tabletGap !== '' ? `gap: ${typeof tabletGap === 'number' ? tabletGap+'px' : tabletGap};` : '',
    ].filter(Boolean);
    if (tabletDecls.length > 0) {
      cssBlocks.push(`@media (min-width: 769px) and (max-width: 1024px) { ${innerSel} { ${tabletDecls.join(' ')} } }`);
    }
  }
  if (tabletMargin) {
    const marginCss = formatBoxSides(tabletMargin, 'margin');
    if (marginCss) {
      cssBlocks.push(`@media (min-width: 769px) and (max-width: 1024px) { ${innerSel}, #wrapper-${validRowId} { ${marginCss} } }`);
    }
  }

  // Desktop edge gutter (>=769px, opt-out via data-edge-bleed)
  // Selector spec: 0,1,1,1 (id + attribute :not + element). Higher than
  // 0,1,0,0, so this wins WITHIN its viewport even though sourced after
  // the base — gives the row its 24px minimum gutter unless explicit
  // opt-out is set. Critical: uses CSS max() so explicit larger desktop
  // padding values are still honoured.
  // Full-bleed rows opt OUT of the forced 24px gutter — a full-width hero/banner
  // must reach the true viewport edges. Their own padding (if any) still applies.
  if (!isFullBleed) {
    cssBlocks.push(
      `@media (min-width: 769px) { ${innerSel}:not([data-edge-bleed="true"]) { ` +
        `padding-left: max(${padObj?.left != null ? `${padObj.left}px` : '0px'}, 24px); ` +
        `padding-right: max(${padObj?.right != null ? `${padObj.right}px` : '0px'}, 24px); ` +
      `} }`
    );
  }

  // Shadow-clip protection (Tier 2 — --style-card-shadow-spread)
  // Cards inside this row often draw outward box-shadows (e.g. Light
  // Skeuomorph at 24px spread, Tech Showcase at 80px). If the next row sits
  // flush below with a solid background, those shadows get clipped at the
  // row seam — the card looks like it has a hard cut-off line.
  // Apply a MINIMUM bottom margin equal to the active preset's
  // --style-card-shadow-spread so adjacent rows always leave room for a
  // shadow tail to fade out. Uses CSS max() so explicit user margin values
  // still win when larger. Opt-out: data-edge-bleed="true" or
  // data-shadow-bleed="true".
  cssBlocks.push(
    `@media (min-width: 769px) { ${innerSel}:not([data-edge-bleed="true"]):not([data-shadow-bleed="true"]) { ` +
      `margin-bottom: max(${marginObj?.bottom != null ? `${marginObj.bottom}px` : '0px'}, var(--style-card-shadow-spread, 0px)); ` +
    `} }`
  );

  // Desktop min-height
  if (desktopMinHeight) {
    cssBlocks.push(`@media (min-width: 1025px) { ${innerSel} { min-height: ${desktopMinHeight}; } }`);
  }

  // Visibility (responsive show/hide)
  interface VisibilityShape { mobile?: boolean; tablet?: boolean; desktop?: boolean }
  const visibility: VisibilityShape | undefined = (data?.visibility as VisibilityShape | undefined) ?? (data?.settings as { visibility?: VisibilityShape } | undefined)?.visibility;
  if (visibility) {
    if (visibility.mobile === false) {
      cssBlocks.push(`@media (max-width: 768px) { ${innerSel} { display: none; } }`);
    }
    if (visibility.tablet === false) {
      cssBlocks.push(`@media (min-width: 769px) and (max-width: 1024px) { ${innerSel} { display: none; } }`);
    }
    if (visibility.desktop === false) {
      cssBlocks.push(`@media (min-width: 1025px) { ${innerSel} { display: none; } }`);
    }
  }

  // Inline styles (LAYOUT ONLY — not responsive-overridable)
  const alignItemsRaw = typeof data?.alignItems === 'string' ? data.alignItems : undefined;
  const alignItems: string | undefined = alignItemsRaw || (data?.forceEqualHeight ? 'stretch' : undefined);
  const textAlign = (typeof data?.textAlign === 'string' ? data.textAlign : undefined) as React.CSSProperties['textAlign'];
  const gapVal: string | number = (typeof data?.gap === 'string' || typeof data?.gap === 'number') ? data.gap : '0';
  const minHeightVal: string | number | undefined = (typeof data?.minHeight === 'string' || typeof data?.minHeight === 'number') ? data.minHeight : undefined;
  const overflowVal: React.CSSProperties['overflow'] = (typeof data?.overflow === 'string' ? data.overflow : 'visible');

  // 2026-05-28 PR G5 — `display: grid` MOVED FROM inline to generated CSS.
  // Inline-style specificity (1,0,0,0) was unconditionally beating the
  // generated `${innerSel}:not(:has(non-empty-leaf)) { display: none; }`
  // row-collapse rule (specificity 0,1,1,0 + :has + :not). Result: the
  // empty hdr-promo-strip row stayed `display: grid` and painted its
  // 12px orange band even though my row-empty selector correctly
  // matched. With `display` + `gap` in generated CSS, both the base
  // `${innerSel} { display: grid }` and the override
  // `${innerSel}:not(:has(...)) { display: none }` are class-tier
  // selectors and the override wins on source order. `gap` follows the
  // same logic so the mobile @media gap rules below can override it.
  // Prepend (unshift) so any subsequent same-selector rule wins source
  // order. MUST happen BEFORE the cssBlocks.join call below.
  const gapCss = typeof gapVal === 'number' ? `${gapVal}px` : gapVal;
  if (isFillCenter) {
    // Flexbox auto-distribute. justify-content:center centers the group when it
    // doesn't fill the row; flex-grow on the children means it usually DOES
    // fill (3 of 5 columns each expand), so centering is the graceful fallback.
    cssBlocks.unshift(`${innerSel} { display: flex; flex-wrap: wrap; justify-content: center; align-items: stretch; gap: ${gapCss}; box-sizing: border-box; }`);
    // flex-basis 0 makes grow ratios authoritative (equal share by default).
    // min-width MUST be 0 here — this is the whole point of "fill & centre":
    // when a column collapses (unused/empty CMS block) the survivors should
    // shrink-to-share and grow to FILL the row, centred, never wrapping onto a
    // second line and never leaving a gap where the missing block was.
    // A non-zero min-width (the old `min(220px, 100%)`) floors each column so
    // that N columns can't fit on one line at narrower widths → flex-wrap drops
    // one onto the next row = the "rigid 220px strips" stacking bug documented
    // in the mobile-reset block above (the live footer bug). That value only
    // ever "worked" because Column.tsx emits an inline `min-width:0` that beat
    // it in the cascade — so on any build/site without that inline override the
    // 220px floor resurfaced the wrap-stack. Set it to 0 at the source instead
    // of relying on an accidental inline override. (An explicit per-column
    // min-width set in the editor still wins via Column.tsx's inline style.)
    cssBlocks.push(`${innerSel} > .is-layout-container { flex: 1 1 0; min-width: 0; }`);
    // Preserve the designer's fr ratios as flex-grow weights, so e.g. a 1.5fr
    // brand column stays proportionally wider than 1fr link columns — and when
    // a column is hidden, the survivors still split the row by their weights.
    columnDefs.forEach((c, i) => {
      const fr = parseFloat(String((c).width ?? '1')) || 1;
      if (fr !== 1) cssBlocks.push(`${innerSel} > .is-layout-container:nth-child(${i + 1}) { flex-grow: ${fr}; }`);
    });
  } else {
    cssBlocks.unshift(`${innerSel} { display: grid; gap: ${gapCss}; box-sizing: border-box; }`);
  }

  const generatedCss = cssBlocks.join('\n');

  const rowStyle: React.CSSProperties = {
    // display + gap + box-sizing intentionally moved to generated CSS above.
    minHeight: minHeightVal,
    alignItems,
    textAlign,
    overflow: overflowVal,
    // Position + zIndex for header rows (mega-menu escape) — not responsive.
    ...(data?.zIndex != null ? { position: 'relative' as const, zIndex: Number(data.zIndex) } : {}),
    ...style,
  };

  const wrapperStyle: React.CSSProperties = {
    width: '100%',
  };

  const gridElement = (
    <div
        id={needsWrapper ? undefined : validRowId}
        style={rowStyle}
        className={`is-layout-container empty:hidden ${typeof data?.customClasses === 'string' ? data.customClasses : ''} ${typeof animationClass === 'string' ? animationClass : ''} ${className ?? ''}`.trim()}
        data-block-type={needsWrapper ? undefined : dataBlockType}
        data-block-id={needsWrapper ? undefined : dataBlockId}
        data-edge-bleed={data?.edgeBleed === true ? 'true' : undefined}
        // 2026-05-21 — shadow-bleed opt-out. Lets a row skip the
        // --style-card-shadow-spread reserve (default 10px). Used by
        // header / footer chrome rows that have solid backgrounds and
        // no internal shadowed cards; without it those rows showed a
        // 10px body-bg-coloured gap between every horizontal slab,
        // which on a near-black background read as a prominent black
        // border between each header row.
        data-shadow-bleed={data?.shadowBleed === true ? 'true' : undefined}
        // 2026-05-28 — preserve-empty opt-out. Lets a row keep its
        // designed height + column tracks even when no leaf block
        // renders content. Used for intentional spacer rows or rows
        // whose CMS blocks are seeded later. Without the opt-out, the
        // empty-row / auto-fit collapse rules above will hide the row
        // outright.
        data-preserve-empty={preserveEmpty ? 'true' : undefined}
    >
      {children}
    </div>
  );

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: generatedCss }} />
      {needsWrapper ? (
        <div
            id={`wrapper-${validRowId}`}
            style={wrapperStyle}
            className={`is-layout-container empty:hidden ${animationClass ? animationClass : ''}`.trim()}
            data-block-type={dataBlockType}
            data-block-id={dataBlockId}
        >
          {React.cloneElement(gridElement as React.ReactElement<{ id?: string }>, { id: validRowId })}
        </div>
      ) : gridElement}
    </>
  );
};
